import { convexQuery } from "@convex-dev/react-query";
import { api } from "@selca/backend/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  isValidPhoneNumber,
  PhoneInput,
} from "@workspace/ui/components/input-otp";
import { toast } from "@workspace/ui/components/sonner";
import { cn } from "@workspace/ui/lib/utils";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { BookOpenIcon, Clock, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigation } from "../App";
import { useAuth } from "../hooks/use-auth";
import { authClient } from "../lib/auth-client";
import { convex } from "../lib/convex";
import selcaIcon from "../selca-icon-min.png";
import { BrandDescription } from "./BrandDescription";
import { BrandHeader } from "./BrandHeader";
import { LiquidGlass } from "./ui/LiquidGlass";
import { LiquidGlassButton } from "./ui/LiquidGlassButton";

const HERO_EASE = [0.22, 1, 0.36, 1] as const;
const SIGN_UP_TIME_SECONDS_ESTIMATE = 25;

const HERO_CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const HERO_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: HERO_EASE,
    },
  },
};

export type AuthStep =
  | "pending"
  | "signed-in"
  | "phone-signed-out"
  | "phone-signed-in"
  | "code";

export type AuthHeroProps = {
  show: boolean;
  onAnimationComplete: () => void;
  onCodeErrorChange: (hasError: boolean) => void;
  onStepChange: (step: AuthStep) => void;
  isLandingPage?: boolean;
};
type TypingPhase = "header" | "desc";

function GoogleSignInButton({ onClick }: { onClick: () => void }) {
  return (
    <LiquidGlassButton
      color="neutral"
      fullWidth
      onClick={onClick}
      type="button"
    >
      <svg
        className="size-5"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      Google
    </LiquidGlassButton>
  );
}

type HeroProps = {
  skipAnimation?: boolean;
  onTypingComplete?: () => void;
  showBrandHeader?: boolean;
  showBrandDescription?: boolean;
  isLandingPage?: boolean;
  /** Glass container opacity (0-100). Controls translucency. */
  glassOpacity?: number;
};

export function Hero({
  skipAnimation = false,
  onTypingComplete,
  showBrandHeader = true,
  showBrandDescription = true,
  isLandingPage = false,
  glassOpacity,
}: HeroProps) {
  const { navigate } = useNavigation();

  const { data: totalUsers } = useQuery({
    ...convexQuery(api.users.getTotalUsers, {}),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const [typing, setTyping] = useState(() => ({
    header: skipAnimation,
    desc: skipAnimation,
  }));

  const markTypingDone = (phase: TypingPhase) => {
    setTyping((prev) => {
      if (prev[phase]) return prev;
      return { ...prev, [phase]: true };
    });
  };

  const allTypingDone = typing.header && typing.desc;

  useEffect(() => {
    if (allTypingDone && !skipAnimation) {
      onTypingComplete?.();
    }
  }, [allTypingDone, skipAnimation, onTypingComplete]);

  const showAuthHero = allTypingDone;

  // HeroActions animates in after AuthHero completes
  const [authHeroAnimated, setAuthHeroAnimated] = useState(skipAnimation);
  const [authStep, setAuthStep] = useState<AuthStep>("pending");
  const [hasCodeError, setHasCodeError] = useState(false);

  return (
    <LayoutGroup id="hero-content">
      <div className="flex w-full max-w-md flex-col items-stretch text-center">
        <LiquidGlass
          blurAmount={12}
          className="mb-4 w-full"
          cornerRadius={24}
          opacity={glassOpacity}
          padding="24px 32px"
          variant={isLandingPage ? "landing" : "default"}
        >
          {showBrandHeader && (
            <BrandHeader
              isLandingPage={isLandingPage}
              onHeaderTypingComplete={() => markTypingDone("header")}
              showBears={typing.header}
              skipAnimation={skipAnimation}
            />
          )}

          {showBrandDescription && (
            <BrandDescription
              isLandingPage={isLandingPage}
              onDescTypingComplete={() => markTypingDone("desc")}
              showBears={typing.header}
              skipAnimation={skipAnimation}
            />
          )}
        </LiquidGlass>

        <LiquidGlass
          blurAmount={12}
          className="w-full"
          cornerRadius={24}
          opacity={glassOpacity}
          padding="16px 24px"
          variant={isLandingPage ? "landing" : "default"}
        >
          <AuthHero
            isLandingPage={isLandingPage}
            onAnimationComplete={() => setAuthHeroAnimated(true)}
            onCodeErrorChange={setHasCodeError}
            onStepChange={setAuthStep}
            show={showAuthHero}
          />

          <HeroActions
            animate={authHeroAnimated}
            authStep={authStep}
            hasCodeError={hasCodeError}
            isLandingPage={isLandingPage}
            navigate={navigate}
            signUpTimeSeconds={SIGN_UP_TIME_SECONDS_ESTIMATE}
            totalUsers={totalUsers}
          />
        </LiquidGlass>
      </div>
    </LayoutGroup>
  );
}

function HeroStats({
  totalUsers,
  signUpTimeSeconds,
  isLandingPage = false,
}: {
  totalUsers?: number;
  signUpTimeSeconds: number;
  isLandingPage?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between text-[13px]",
        isLandingPage ? "landing-text-secondary" : "text-muted-foreground"
      )}
    >
      <div className="flex items-center gap-1.5">
        <Users className="size-3.5" />
        <span>
          <span
            className={cn(
              "font-semibold",
              isLandingPage ? "landing-text-primary" : "text-foreground"
            )}
          >
            {totalUsers ?? "..."}
          </span>{" "}
          {totalUsers === 1 ? "user" : "users"} joined
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Clock className="size-3.5" />
        <span>~{signUpTimeSeconds}s time to sign up</span>
      </div>
    </div>
  );
}

function HeroActions({
  animate,
  authStep,
  hasCodeError,
  totalUsers,
  signUpTimeSeconds,
  navigate,
  isLandingPage = false,
}: {
  animate: boolean;
  authStep: AuthStep;
  hasCodeError: boolean;
  totalUsers?: number;
  signUpTimeSeconds: number;
  navigate: (path: string) => void;
  isLandingPage?: boolean;
}) {
  const getMarginTop = () => {
    switch (authStep) {
      case "phone-signed-out":
        return "-mt-0";
      case "phone-signed-in":
        return "-mt-20";
      case "code":
        return hasCodeError ? "mt-4" : "-mt-2";
      case "signed-in":
        return "-mt-12";
      default:
        return "-mt-4";
    }
  };

  return (
    <motion.div
      animate={animate ? "visible" : "hidden"}
      className={cn("mx-auto flex w-full flex-col gap-3", getMarginTop())}
      initial="hidden"
      variants={HERO_CONTAINER_VARIANTS}
    >
      <motion.div className="flex w-full gap-3" variants={HERO_ITEM_VARIANTS}>
        <LiquidGlassButton
          className="flex-1"
          color="sage"
          onClick={() => navigate("/docs")}
        >
          <BookOpenIcon className="mr-2 size-4" />
          Read Docs
        </LiquidGlassButton>
        <LiquidGlassButton
          className="flex-1 text-sm"
          color="blush"
          onClick={() => navigate("/contact")}
        >
          <div className="mr-2 flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-rose-400 to-rose-500 dark:from-rose-300 dark:to-rose-400">
            <img
              alt="Selca"
              className="h-[28px] w-[28px] object-contain"
              src={selcaIcon}
            />
          </div>
          Add Contact
        </LiquidGlassButton>
      </motion.div>

      <motion.div variants={HERO_ITEM_VARIANTS}>
        <HeroStats
          isLandingPage={isLandingPage}
          signUpTimeSeconds={signUpTimeSeconds}
          totalUsers={totalUsers}
        />
      </motion.div>
    </motion.div>
  );
}

export function AuthHero({
  show,
  onAnimationComplete,
  onCodeErrorChange,
  onStepChange,
  isLandingPage = false,
}: AuthHeroProps) {
  const queryClient = useQueryClient();
  const {
    session,
    profile,
    isPending,
    isAuthenticated,
    isAuthLoading,
    isPhoneLinked,
  } = useAuth();

  const { data: myAccounts } = useQuery({
    ...convexQuery(api.users.getMyAccounts, {}),
    enabled: isAuthenticated && !isAuthLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wasAuthenticated = useRef(isAuthenticated);
  const prevStep = useRef<"phone" | "code">("phone");

  useEffect(() => {
    if (isPending) {
      onStepChange("pending");
      return;
    }
    if (session?.user && isPhoneLinked) {
      onStepChange("signed-in");
      return;
    }

    if (prevStep.current === "code" && step === "phone") {
      onCodeErrorChange(false);
    }

    prevStep.current = step;

    const stepValue =
      session?.user && !isPhoneLinked ? "phone-signed-in" : "phone-signed-out";
    onStepChange(step === "code" ? "code" : stepValue);
  }, [
    isPending,
    session,
    isPhoneLinked,
    step,
    onStepChange,
    onCodeErrorChange,
  ]);

  useEffect(() => {
    if (isAuthLoading) return;

    const justSignedOut = wasAuthenticated.current && !isAuthenticated;
    if (justSignedOut) {
      setStep("phone");
      setPhoneNumber("");
      setCode("");
      setError(null);
    }

    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, isAuthLoading]);

  const providers =
    myAccounts?.map((a: { providerId?: string | null }) => {
      const raw = a.providerId ?? "unknown";
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    }) ?? [];

  const handleGoogleSignIn = () => {
    authClient.signIn.social({ provider: "google" });
  };

  const handleSignOut = async () => {
    await authClient.signOut();
    queryClient.clear();
  };

  const handleSendOTP = async () => {
    if (!isValidPhoneNumber(phoneNumber)) {
      setError("Please enter a valid phone number");
      return;
    }

    setIsLoading(true);
    setError(null);

    const { error } = await authClient.phoneNumber.sendOtp({ phoneNumber });

    setIsLoading(false);

    if (error) {
      setError(error.message || "Failed to send code");
      return;
    }

    setStep("code");
  };

  const handleVerifyOTP = async () => {
    if (code.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    setIsLoading(true);
    setError(null);
    onCodeErrorChange(false);

    const { error } = await authClient.phoneNumber.verify({
      phoneNumber,
      code,
      updatePhoneNumber: Boolean(session?.user) && !isPhoneLinked,
    });

    if (error) {
      setIsLoading(false);
      setError(error.message || "Invalid code");
      onCodeErrorChange(true);
      return;
    }

    try {
      await convex.mutation(api.users.syncUserPhoneLink, {});
      queryClient.invalidateQueries({
        queryKey: convexQuery(api.users.getMyProfile, {}).queryKey,
      });
    } catch {
      toast.error("Failed to link phone number");
    }

    setIsLoading(false);
    setStep("phone");
    setPhoneNumber("");
    setCode("");
  };

  const canSend = isValidPhoneNumber(phoneNumber) && !isLoading;
  const canVerify = code.length === 6 && !isLoading;

  const SLIDE_X = 16;
  const SLIDE_Y = 8;

  return (
    <div className="h-[15rem]">
      <motion.div
        animate={show ? { opacity: 1 } : { opacity: 0 }}
        className={`mx-auto mt-4 flex h-full w-full flex-col gap-4 ${
          show ? "" : "pointer-events-none"
        }`}
        initial={{ opacity: 0 }}
        layout
        onAnimationComplete={() => {
          if (show) onAnimationComplete();
        }}
        transition={{
          duration: 0.5,
          ease: HERO_EASE,
          layout: { duration: 0.4, ease: HERO_EASE },
        }}
      >
        {/* Auth block */}
        <motion.div className="w-full">
          <AnimatePresence initial={false} mode="wait">
            {isPending ? (
              <motion.div
                animate={{ opacity: 1 }}
                className="w-full"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                key="pending"
              />
            ) : session?.user && isPhoneLinked ? (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="flex w-full flex-col items-center justify-center gap-4"
                exit={{ opacity: 0, y: -SLIDE_Y }}
                initial={{ opacity: 0, y: SLIDE_Y }}
                key="signed-in"
                layout
                transition={{ layout: { duration: 0.4, ease: HERO_EASE } }}
              >
                <div className="flex flex-col items-center gap-1">
                  <p className="text-sm text-foreground">
                    Signed in as{" "}
                    <span className="font-medium">
                      {session.user.name ||
                        session.user.email ||
                        profile?.phoneNumber ||
                        "Unknown user"}
                    </span>
                  </p>
                  {providers.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      via {providers.join(", ")}
                    </p>
                  )}
                </div>

                <LiquidGlassButton
                  color="neutral"
                  onClick={handleSignOut}
                  size="sm"
                >
                  Sign out
                </LiquidGlassButton>
              </motion.div>
            ) : (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="w-full space-y-4"
                exit={{ opacity: 0, y: 4 }}
                initial={{ opacity: 0, y: 4 }}
                key="auth-form"
                transition={{ duration: 0.2 }}
              >
                {session?.user && !isPhoneLinked && (
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Finish setup by linking your phone number
                  </p>
                )}

                <AnimatePresence initial={false} mode="wait">
                  {step === "phone" ? (
                    <motion.div
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-4"
                      exit={{ opacity: 0, x: -SLIDE_X }}
                      initial={{ opacity: 0, x: SLIDE_X }}
                      key="phone-step"
                      transition={{ duration: 0.45, ease: HERO_EASE }}
                    >
                      <PhoneInput
                        defaultCountry="US"
                        disabled={isLoading}
                        glassEffect
                        onChange={setPhoneNumber}
                        slotSize="xs"
                        value={phoneNumber}
                      />

                      <LiquidGlassButton
                        color="primary"
                        disabled={!canSend}
                        fullWidth
                        onClick={handleSendOTP}
                      >
                        {isLoading ? "Sending..." : "Send verification code"}
                      </LiquidGlassButton>

                      {!session?.user && (
                        <>
                          <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center">
                              <span
                                className={cn(
                                  "w-full border-t",
                                  isLandingPage && "border-white/20"
                                )}
                              />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span
                                className={cn(
                                  "px-2",
                                  isLandingPage
                                    ? "text-foreground/70"
                                    : "bg-background text-muted-foreground"
                                )}
                              >
                                Or continue with
                              </span>
                            </div>
                          </div>

                          <GoogleSignInButton onClick={handleGoogleSignIn} />
                        </>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-4"
                      exit={{ opacity: 0, x: SLIDE_X }}
                      initial={{ opacity: 0, x: -SLIDE_X }}
                      key="code-step"
                      transition={{ duration: 0.45, ease: HERO_EASE }}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <InputOTP
                          autoFocus
                          disabled={isLoading}
                          maxLength={6}
                          onChange={setCode}
                          value={code}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot glassEffect index={0} />
                            <InputOTPSlot glassEffect index={1} />
                            <InputOTPSlot glassEffect index={2} />
                            <InputOTPSlot glassEffect index={3} />
                            <InputOTPSlot glassEffect index={4} />
                            <InputOTPSlot glassEffect index={5} />
                          </InputOTPGroup>
                        </InputOTP>

                        <p className="text-center text-xs text-foreground">
                          Enter the 6-digit code sent to {phoneNumber}
                        </p>
                      </div>

                      {error && (
                        <motion.p
                          animate={{ opacity: 1, y: 0 }}
                          className="text-center text-sm font-medium text-destructive"
                          initial={{ opacity: 0, y: -SLIDE_Y }}
                        >
                          {error}
                        </motion.p>
                      )}

                      <button
                        className="w-full text-sm text-foreground hover:underline"
                        onClick={() => setStep("phone")}
                        type="button"
                      >
                        Change phone number
                      </button>

                      <LiquidGlassButton
                        color="primary"
                        disabled={!canVerify}
                        fullWidth
                        onClick={handleVerifyOTP}
                      >
                        {isLoading ? "Verifying..." : "Verify code"}
                      </LiquidGlassButton>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </div>
  );
}
